import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { PRODUCT_CATEGORIES } from '@/features/product-library/constants';
import { logger } from '@/lib/logger';

interface UnlinkResult {
  success: boolean;
  productsConverted: number;
  controlsConverted: number;
  recordsDeleted: number;
  error?: string;
}

export class PortalUnlinkService {
  private async getClient() {
    return getAuthenticatedSupabaseClient();
  }

  /**
   * Unlink a Portal.io proposal from a floor plan
   * - Removes all portal_imported_products records
   * - Converts placed products to generic (removes Portal metadata)
   * - Keeps products on floor plan as non-Portal items
   */
  async unlinkProposal(
    organizationId: string,
    floorPlanId?: string,
    proposalId?: string,
  ): Promise<UnlinkResult> {
    try {
      const supabase = await this.getClient();
      // Step 1: Get all placed lv_products that are from Portal
      // CRITICAL: Filter by organization to prevent cross-tenant data access
      let placedProductsQuery = supabase
        .from('lv_products')
        .select(
          `
          *,
          floor_plans!inner(
            id,
            projects!inner(
              id,
              clerk_org_id
            )
          )
        `,
        )
        .eq('metadata->from_portal', true)
        .eq('floor_plans.projects.clerk_org_id', organizationId);

      if (floorPlanId) {
        placedProductsQuery = placedProductsQuery.eq('floor_plan_id', floorPlanId);
      }

      const { data: placedProducts, error: fetchError } = await placedProductsQuery;

      if (fetchError) {
        logger.error('Error fetching placed products:', fetchError);
        throw new Error('Failed to fetch placed products');
      }

      // Step 2: Convert placed products to generic (remove Portal linkage)
      let productsConverted = 0;
      if (placedProducts && placedProducts.length > 0) {
        for (const product of placedProducts) {
          const updatedMetadata = {
            ...(product.metadata || {}),
            from_portal: false,
            portal_product_id: null,
            converted_to_generic: true,
            unlinked_at: new Date().toISOString(),
            original_portal_data: product.metadata, // Preserve original data for reference
          };

          // Get the category display name
          const categoryDisplayName = product.category
            ? PRODUCT_CATEGORIES[product.category as keyof typeof PRODUCT_CATEGORIES] ||
              product.category
            : 'Product';

          // CRITICAL: Only update products that belong to this organization
          // Since we already filtered by org in the select query, we can safely update
          const { error: updateError } = await supabase
            .from('lv_products')
            .update({
              product_id: null, // Unlink from product_library
              name: categoryDisplayName, // Set name to category display name
              metadata: updatedMetadata,
            })
            .eq('id', product.id);

          if (updateError) {
            logger.error('Error updating product:', updateError);
          } else {
            productsConverted++;
          }
        }
      }

      // Step 2B: Get all placed lighting_controls that are from Portal
      // CRITICAL: Filter by organization to prevent cross-tenant data access
      let placedControlsQuery = supabase
        .from('lighting_controls')
        .select(
          `
          *,
          floor_plans!inner(
            id,
            projects!inner(
              id,
              clerk_org_id
            )
          )
        `,
        )
        .eq('category_info->from_portal', true)
        .eq('floor_plans.projects.clerk_org_id', organizationId);

      if (floorPlanId) {
        placedControlsQuery = placedControlsQuery.eq('floor_plan_id', floorPlanId);
      }

      const { data: placedControls, error: controlsFetchError } = await placedControlsQuery;

      if (controlsFetchError) {
        logger.error('Error fetching placed controls:', controlsFetchError);
      }

      // Step 2C: Convert placed controls to generic (remove Portal linkage)
      let controlsConverted = 0;
      if (placedControls && placedControls.length > 0) {
        for (const control of placedControls) {
          const updatedCategoryInfo = {
            ...(control.category_info || {}),
            from_portal: false,
            portal_product_id: null,
            converted_to_generic: true,
            unlinked_at: new Date().toISOString(),
            original_portal_data: control.category_info, // Preserve original data for reference
          };

          // CRITICAL: Only update controls that belong to this organization
          // Since we already filtered by org in the select query, we can safely update
          const { error: updateError } = await supabase
            .from('lighting_controls')
            .update({
              product_id: null, // Unlink from product_library
              category_info: updatedCategoryInfo,
            })
            .eq('id', control.id);

          if (updateError) {
            logger.error('Error updating control:', updateError);
          } else {
            controlsConverted++;
          }
        }
      }

      // Step 3: Delete all portal_imported_products records
      let deleteQuery = supabase
        .from('portal_imported_products')
        .delete()
        .eq('clerk_org_id', organizationId);

      if (floorPlanId) {
        deleteQuery = deleteQuery.eq('floor_plan_id', floorPlanId);
      }

      if (proposalId) {
        deleteQuery = deleteQuery.eq('proposal_id', proposalId);
      }

      // Get count before deletion
      const { count: recordsToDelete } = await supabase
        .from('portal_imported_products')
        .select('*', { count: 'exact', head: true })
        .eq('clerk_org_id', organizationId)
        .match(floorPlanId ? { floor_plan_id: floorPlanId } : {})
        .match(proposalId ? { proposal_id: proposalId } : {});

      const { error: deleteError } = await deleteQuery;

      if (deleteError) {
        logger.error('Error deleting imported products:', deleteError);
        throw new Error('Failed to delete imported products');
      }

      // Step 4: Optionally clean up product_library entries
      // We'll keep them for now as they might be useful for other floor plans
      // If needed, this can be uncommented:
      /*
      const { error: libraryError } = await supabase
        .from('product_library')
        .delete()
        .eq('organization_id', organizationId)
        .eq('source', 'portal')
        .is('referenced_item_id', null); // Only delete if not referenced elsewhere
      
      if (libraryError) {
        logger.error('Error cleaning product library:', libraryError);
      }
      */

      return {
        success: true,
        productsConverted,
        controlsConverted,
        recordsDeleted: recordsToDelete || 0,
      };
    } catch (error) {
      logger.error('Error unlinking proposal:', error);
      return {
        success: false,
        productsConverted: 0,
        controlsConverted: 0,
        recordsDeleted: 0,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Check if a floor plan has any linked Portal proposal
   */
  async hasLinkedProposal(organizationId: string, floorPlanId?: string): Promise<boolean> {
    try {
      const supabase = await this.getClient();
      let query = supabase
        .from('portal_imported_products')
        .select('id', { count: 'exact', head: true })
        .eq('clerk_org_id', organizationId);

      if (floorPlanId) {
        query = query.eq('floor_plan_id', floorPlanId);
      }

      const { count, error } = await query;

      if (error) {
        logger.error('Error checking proposal link:', error);
        return false;
      }

      return (count || 0) > 0;
    } catch (error) {
      logger.error('Error checking proposal link:', error);
      return false;
    }
  }
}

export const portalUnlinkService = new PortalUnlinkService();
